// src/utils/rfidTagUtils.js
/**
 * Utilidades para el blindaje y gestión de tarjetas RFID / Tags UHF
 * Tiempo del Centro de México (CST/CDT)
 */

export const TAG_KEYS = ['tag', 'tag2', 'tag3', 'tag4', 'tag5', 'tag6', 'tag7', 'tag8', 'tag9'];

export const getTagLabel = (key) => {
  const idx = TAG_KEYS.indexOf(key);
  return idx >= 0 ? `Tag ${idx + 1}` : 'Tag';
};

/**
 * Convierte un número impreso en el plástico del Tag UHF (ej: "05526479" o "5526479")
 * al formato Wiegand que detecta la antena ZKTeco / panel Hikvision (ej: "08421455").
 * Estructura: 3 dígitos Facility Code (bits 16-23) + 5 dígitos Card Number (bits 0-15).
 */
export function convertirTagImpresoAAntena(impreso) {
  if (!impreso) return null;
  const clean = String(impreso).trim();
  if (!/^\d+$/.test(clean)) return null;
  const num = parseInt(clean, 10);
  if (isNaN(num) || num <= 0 || num > 0xFFFFFF) return null;
  const fc = (num >> 16) & 0xFF;
  const cn = num & 0xFFFF;
  return `${String(fc).padStart(3, '0')}${String(cn).padStart(5, '0')}`;
}

/**
 * Convierte el código detectado por la antena (ej: "08421455")
 * a su número impreso en el plástico del Tag UHF (ej: "05526479").
 */
export function convertirTagAntenaAImpreso(antena) {
  if (!antena) return null;
  const clean = String(antena).trim();
  if (!/^\d+$/.test(clean)) return null;
  let fc, cn;
  if (clean.length === 8) {
    fc = parseInt(clean.slice(0, 3), 10);
    cn = parseInt(clean.slice(3), 10);
  } else if (clean.length === 7) {
    fc = parseInt(clean.slice(0, 2), 10);
    cn = parseInt(clean.slice(2), 10);
  } else {
    return null;
  }
  if (isNaN(fc) || isNaN(cn) || fc > 255 || cn > 65535) return null;
  const num = (fc << 16) | cn;
  return String(num).padStart(8, '0');
}

/**
 * Limpia y normaliza una cadena de Tag
 */
export function cleanTagValue(val) {
  if (val === null || val === undefined) return '';
  return String(val).trim();
}

/**
 * Obtiene todos los equivalentes posibles de un tag (valor directo, formato antena, formato impreso)
 */
export function getTagEquivalents(tagVal) {
  const clean = cleanTagValue(tagVal);
  if (!clean) return [];
  const results = new Set([clean.toLowerCase()]);
  const antena = convertirTagImpresoAAntena(clean);
  if (antena) results.add(antena.toLowerCase());
  const impreso = convertirTagAntenaAImpreso(clean);
  if (impreso) results.add(impreso.toLowerCase());
  return Array.from(results);
}

/**
 * Audita todas las casas de un fraccionamiento y genera:
 * 1. map: Mapa de cada tag normalizado a la información de la casa donde reside.
 * 2. duplicateConflicts: Lista de tags que ya están duplicados en la base de datos entre distintas viviendas.
 */
export function auditFraccionamientoTags(casasList = [], excludeCasaId = null) {
  const tagToCasaMap = new Map();
  const duplicateConflicts = []; // Array de { tag: string, casas: [{ casaId, numeroCasa, nombre, tagKey }] }
  const seenTagsInDb = new Map(); // normalizado -> array de asignaciones

  casasList.forEach((casa) => {
    const casaId = casa.id;
    const numCasa = String(casa.numero || casa.numeroCasa || casa.lote || casaId).trim();
    const nombre = String(casa.nombre_residente || casa.nombre || casa.nombreCompleto || 'Residente').trim();

    // Revisar los 9 slots
    TAG_KEYS.forEach((tk) => {
      const val = cleanTagValue(casa[tk]);
      if (val) {
        const item = {
          casaId,
          numeroCasa: numCasa,
          nombreResidente: nombre,
          tagKey: tk,
          tagLabel: getTagLabel(tk),
          tagValor: val
        };

        const eqList = getTagEquivalents(val);
        eqList.forEach((eq) => {
          if (!seenTagsInDb.has(eq)) {
            seenTagsInDb.set(eq, []);
          }
          const prevList = seenTagsInDb.get(eq);
          // Si ya existe en otra casa distinta
          const isFromOtherCasa = prevList.some(p => p.casaId !== casaId);
          if (isFromOtherCasa && !duplicateConflicts.some(dc => dc.tagNorm === eq)) {
            duplicateConflicts.push({
              tagNorm: eq,
              tagOriginal: val,
              casas: [...prevList, item]
            });
          }
          prevList.push(item);
        });

        // Registrar en tagToCasaMap si no es la casa excluida
        if (casaId !== excludeCasaId) {
          eqList.forEach((eq) => {
            if (!tagToCasaMap.has(eq)) {
              tagToCasaMap.set(eq, item);
            }
          });
        }
      }
    });

    // Revisar residentes adicionales si existen
    if (Array.isArray(casa.residentes)) {
      casa.residentes.forEach((r, idx) => {
        const rTag = cleanTagValue(r.tagRfid || r.rfid || r.tag);
        if (rTag) {
          const item = {
            casaId,
            numeroCasa: numCasa,
            nombreResidente: String(r.nombre || nombre).trim(),
            tagKey: `residente_${idx}`,
            tagLabel: `Residente Adicional (${r.parentesco || idx + 1})`,
            tagValor: rTag
          };

          const eqList = getTagEquivalents(rTag);
          eqList.forEach((eq) => {
            if (!seenTagsInDb.has(eq)) {
              seenTagsInDb.set(eq, []);
            }
            const prevList = seenTagsInDb.get(eq);
            const isFromOtherCasa = prevList.some(p => p.casaId !== casaId);
            if (isFromOtherCasa && !duplicateConflicts.some(dc => dc.tagNorm === eq)) {
              duplicateConflicts.push({
                tagNorm: eq,
                tagOriginal: rTag,
                casas: [...prevList, item]
              });
            }
            prevList.push(item);
          });

          if (casaId !== excludeCasaId) {
            eqList.forEach((eq) => {
              if (!tagToCasaMap.has(eq)) {
                tagToCasaMap.set(eq, item);
              }
            });
          }
        }
      });
    }
  });

  return {
    tagToCasaMap,
    duplicateConflicts
  };
}

/**
 * Normaliza una clave de encabezado para tolerar acentos, mayúsculas y guiones
 */
export function normalizeColumnKey(str) {
  if (!str) return '';
  return String(str)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}
